import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { ArrowUpRight, Award, MapPin, Sparkles } from "lucide-react";

/**
 * Wall of Explorers — public social-proof gallery of the latest travellers
 * who downloaded their Instagram badge after completing a hunt. Names are
 * blurred client-side via CSS so the LAYOUT of a real name is visible
 * (credibility) while privacy is preserved. Auto-refreshes every 60s so a
 * host can leave it open on a screen at the front desk.
 */

function timeAgo(iso, IT) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!t) return "";
  const s = Math.max(1, Math.round((Date.now() - t) / 1000));
  const rules = IT
    ? [[60, "s"], [3600, "min"], [86400, "h"], [604800, "g"]]
    : [[60, "s"], [3600, "min"], [86400, "h"], [604800, "d"]];
  const [rSec] = rules.find(([threshold]) => s < threshold) || [];
  if (rSec === 60) return IT ? `${s}s fa` : `${s}s ago`;
  if (rSec === 3600) return IT ? `${Math.floor(s / 60)}m fa` : `${Math.floor(s / 60)}m ago`;
  if (rSec === 86400) return IT ? `${Math.floor(s / 3600)}h fa` : `${Math.floor(s / 3600)}h ago`;
  if (rSec === 604800) return IT ? `${Math.floor(s / 86400)}g fa` : `${Math.floor(s / 86400)}d ago`;
  return IT ? `${Math.floor(s / 604800)}sett fa` : `${Math.floor(s / 604800)}w ago`;
}

const AVATAR_TONES = ["#a13a2a", "#8a5a2b", "#c55a3c", "#5a7f4a", "#3a2a17", "#6b4a72"];

export default function WallOfBadges() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWall = useMemo(() => async () => {
    try {
      const r = await api.get("/hunt-badges/wall?limit=20");
      setData(r.data);
    } catch { /* silent — wall is decorative */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchWall();
    document.title = IT
      ? "Muro degli Esploratori — Chi ha completato la Mappa | HospitalityHub"
      : "Wall of Explorers — Who completed the Map | HospitalityHub";
    // Live-update every 60s so a display running at the reception feels
    // alive and up-to-date without needing a manual refresh.
    const t = setInterval(fetchWall, 60000);
    return () => clearInterval(t);
  }, [fetchWall, IT]);

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="min-h-screen bg-background">
      <Navbar/>
      <section className="max-w-6xl mx-auto px-6 py-16 sm:py-20" data-testid="wall-of-badges">
        <div className="text-center mb-14">
          <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold flex items-center justify-center gap-2">
            <Award className="w-3.5 h-3.5"/> {IT ? "Muro degli Esploratori" : "Wall of Explorers"}
          </div>
          <h1 className="font-display font-normal italic leading-[0.95] mb-5" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
            {IT ? "L'ultima carovana" : "The latest caravan"}
          </h1>
          <p className="text-muted-foreground italic max-w-2xl mx-auto text-lg leading-relaxed">
            {IT
              ? "Gli ultimi viaggiatori che hanno completato una Mappa del Tesoro e portato a casa il proprio badge. Nomi offuscati per pudore, ma sono tutti veri."
              : "The latest travellers who completed a Treasure Map and took their badge home. Names blurred out of decency, but every one is real."}
          </p>
          {total > 0 && (
            <div className="inline-flex items-center gap-2 mt-6 text-[13px] bg-primary/5 border border-primary/25 rounded-full px-4 py-2" data-testid="wall-total-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
              <span>
                <b className="text-primary" data-testid="wall-total-count">{total.toLocaleString(IT ? "it-IT" : "en-US")}</b>{" "}
                {IT ? "badge scaricati in totale" : "badges downloaded in total"}
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground italic py-20">{IT ? "Caricamento…" : "Loading…"}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-lg max-w-xl mx-auto">
            <div className="text-muted-foreground italic mb-4">
              {IT ? "Nessuno ha ancora scaricato il proprio badge." : "Nobody has downloaded their badge yet."}
            </div>
            <Link to="/caccia-al-tesoro"
              className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
              data-testid="wall-cta-empty">
              <Sparkles className="w-4 h-4"/> {IT ? "Sii il primo — scegli una Mappa" : "Be the first — pick a Map"}
            </Link>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {items.map((it, idx) => {
                const tone = AVATAR_TONES[idx % AVATAR_TONES.length];
                return (
                  <Link
                    key={idx}
                    to={`/caccia-al-tesoro/${it.hunt_id}`}
                    className="group relative block aspect-[4/5] overflow-hidden rounded-sm shadow-md lift"
                    data-testid={`wall-card-${idx}`}
                  >
                    <img
                      src={it.hero_image}
                      alt={it.hunt_title || ""}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.15) 25%, rgba(35,31,27,0.92) 100%)" }}/>
                    <div className="absolute top-3 right-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ring-2 ring-white/40" style={{ background: tone }}>
                        {it.initial}
                      </div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                      <div className="text-[10px] uppercase tracking-[0.3em] opacity-80 mb-1.5 flex items-center gap-1.5">
                        <MapPin className="w-3 h-3"/>{it.hunt_city}
                      </div>
                      <div className="font-display italic text-xl leading-tight line-clamp-2">{it.hunt_title}</div>
                      <div className="mt-3 flex items-center gap-2 text-[11px] opacity-90">
                        <span
                          className="italic select-none"
                          data-testid={`wall-name-${idx}`}
                          style={{ filter: "blur(4px)", WebkitFilter: "blur(4px)", letterSpacing: "0.02em" }}
                          aria-label={IT ? "Nome offuscato" : "Blurred name"}
                          title={IT ? "Nome offuscato per privacy" : "Name blurred for privacy"}
                        >
                          {it.display_name}
                        </span>
                        <span className="opacity-70" data-testid={`wall-time-${idx}`}>· {timeAgo(it.created_at, IT)}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-12">
              <Link
                to="/caccia-al-tesoro"
                className="inline-flex items-center gap-2 text-sm px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all lift"
                data-testid="wall-cta-explore"
              >
                <Sparkles className="w-4 h-4"/> {IT ? "Scegli la tua Mappa" : "Pick your own Map"} <ArrowUpRight className="w-4 h-4"/>
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
